/**
 * Ability VFX config + pure lifecycle (#577) — the PURE half of the new
 * `AbilityVisualManager`, split from its Pixi wiring (`abilityVisualManager.ts`)
 * the exact same way `combatText.ts`/`combatTextManager.ts` split (#48): the
 * animation math + config resolution are unit-testable without Pixi.
 *
 * SCOPE (#577 locked list): a fixed, short-flash-only VFX layer for NINE
 * abilities — cast/impact flashes + ground-target AOE shape indicators.
 * Graphics/Text-only, zero new binary assets. Explicitly NOT in scope:
 * buff-duration-tied persistent visuals (Demonic Circle / Ice Barrier / Stay
 * Mad — split to #598), real icon art, any ability outside the locked list.
 *
 * GEOMETRY IS CONTENT-DERIVED, NEVER HAND-COPIED: `AREA_GEOMETRY_BY_SLUG`
 * reads each locked ability's authored `area` block (radius, or cone
 * arc/range) straight from its JSON at build time — same eager
 * `import.meta.glob` + narrow-schema-strip pattern `abilityFacts.ts` and
 * `abilityCatalog.ts` already use — so a balance edit to e.g. Frost Nova's
 * radius can never silently desync the drawn indicator from the real AOE.
 *
 * GROUND-TARGETED ORIGIN (#629): `WIRE_ENTITY_SCHEMA.cast.aimPoint` is the
 * resolved ground point for a point-aim (skillshot) cast — the SAME
 * `castState.aim` (`kind: "point"`) the sim itself already hashes at
 * cast-start (`apps/server/src/wire.ts`), so a `targetType: "ground"`
 * ability (Meteor / Rain of Arrows / Shadowfury) anchors its indicator at
 * the REAL impact point (`origin: "aimPoint"`) instead of the caster's own
 * position. (Previously these approximated with `origin: "caster"` — see
 * #629/#577 history — before the wire carried a real ground point.)
 * `origin: "caster"` remains correct for the genuinely self-centered
 * abilities (Frost Nova, Cone of Cold). `origin: "target"` is used only for
 * the two abilities with a REAL entity-locked target already on the wire
 * (Bandage → ally, Unstable Affliction → enemy).
 */

import { z } from "zod";
import { SLUG_SCHEMA, type AbilityId, type ChampionId } from "@pit/protocol";
import { ABILITY_META_BY_ID, CHAMPION_SLOTS } from "./abilityCatalog.js";

// ─── Archetypes ──────────────────────────────────────────────────────────

/** The five Graphics/Text-only visual archetypes locked in #577. */
export type AbilityVisualArchetype =
  | "groundCircle"
  | "groundWedge"
  | "floatGlyph"
  | "floatShield"
  | "floatSpeechBubble";

interface GroundCircleVisualDef {
  readonly archetype: "groundCircle";
  /** "caster" for a self-centered nova (Frost Nova), "aimPoint" for a
   *  ground-targeted skillshot resolving to the real impact point (Meteor /
   *  Rain of Arrows / Shadowfury, #629) — kept as a field (not hardcoded in
   *  the manager) so a future ability's anchor is a data change, not a code
   *  change. */
  readonly origin: "caster" | "target" | "aimPoint";
  readonly color: number;
  readonly alpha: number;
  readonly durationMs: number;
  readonly radiusUnits: number;
}

interface GroundWedgeVisualDef {
  readonly archetype: "groundWedge";
  readonly origin: "caster" | "target";
  readonly color: number;
  readonly alpha: number;
  readonly durationMs: number;
  /** Wedge reach (world units) — content's `maxRangeUnits`. */
  readonly rangeUnits: number;
  /** Half-angle as a bp cosine, same representation as `AREA_CONE_SCHEMA`. */
  readonly arcCosThresholdBp: number;
}

interface FloatGlyphVisualDef {
  readonly archetype: "floatGlyph";
  readonly origin: "caster" | "target";
  readonly color: number;
  readonly durationMs: number;
  readonly glyph: string;
}

interface FloatShieldVisualDef {
  readonly archetype: "floatShield";
  readonly origin: "caster" | "target";
  readonly color: number;
  readonly durationMs: number;
}

interface FloatSpeechBubbleVisualDef {
  readonly archetype: "floatSpeechBubble";
  readonly origin: "caster" | "target";
  readonly color: number;
  readonly durationMs: number;
  readonly glyph: string;
}

export type AbilityVisualDef =
  | GroundCircleVisualDef
  | GroundWedgeVisualDef
  | FloatGlyphVisualDef
  | FloatShieldVisualDef
  | FloatSpeechBubbleVisualDef;

// ─── Duration floor (#577 scope item 4) ─────────────────────────────────────

/**
 * Hard floor, ms — nothing may be so short a human can barely perceive it.
 * Every duration below is `>=` this. Shadowfury's originally-named 150-200ms
 * is clamped up to the floor per the issue's explicit instruction.
 */
export const MIN_VISUAL_DURATION_MS = 200;

/** Default duration for the "quick flash" ground-AOE / floating-glyph
 *  archetypes — safely inside the specified 150-300ms range. */
const DEFAULT_DURATION_MS = 250;

// ─── Content-derived geometry (radius / cone arc+range), by ability slug ────

const AREA_GEOMETRY_SCHEMA = z.object({
  slug: SLUG_SCHEMA,
  area: z
    .union([
      z.object({
        shape: z.literal("circle").optional(),
        radius: z.number().int().optional(),
      }),
      z.object({
        shape: z.literal("cone"),
        arcCosThresholdBp: z.number().int(),
        maxRangeUnits: z.number().int(),
      }),
    ])
    .optional(),
});

interface AreaGeometry {
  readonly kind: "circle" | "cone";
  readonly radiusUnits?: number;
  readonly arcCosThresholdBp?: number;
  readonly maxRangeUnits?: number;
}

/**
 * Build-time map of every on-disk ability JSON → its authored `area`
 * geometry, keyed by slug. Same literal-`import.meta.glob` rule as
 * `abilityFacts.ts`/`abilityCatalog.ts`: the call MUST be the literal form
 * for Vite's static transform. Exported for tests only.
 */
export function loadAreaGeometryBySlug(): ReadonlyMap<string, AreaGeometry> {
  const modules = import.meta.glob<string>(
    "../../../../packages/content/data/abilities/*.json",
    { eager: true, query: "?raw", import: "default" },
  );
  const out = new Map<string, AreaGeometry>();
  for (const path of Object.keys(modules).sort()) {
    const raw = modules[path];
    if (raw === undefined) continue;
    const parsed = AREA_GEOMETRY_SCHEMA.parse(JSON.parse(raw) as unknown);
    if (parsed.area === undefined) continue;
    if (parsed.area.shape === "cone") {
      out.set(parsed.slug, {
        kind: "cone",
        arcCosThresholdBp: parsed.area.arcCosThresholdBp,
        maxRangeUnits: parsed.area.maxRangeUnits,
      });
    } else if (parsed.area.radius !== undefined) {
      out.set(parsed.slug, { kind: "circle", radiusUnits: parsed.area.radius });
    }
  }
  return out;
}

const AREA_GEOMETRY_BY_SLUG: ReadonlyMap<string, AreaGeometry> = loadAreaGeometryBySlug();

/** Fail LOUD (build/test time), mirroring `abilityFacts.ts`'s/`abilityCatalog.
 *  ts`'s "malformed content throws here" convention — a locked-list ability
 *  losing its circle radius would otherwise silently draw a 0-radius (or
 *  NaN) indicator. */
function requireCircleRadius(slug: string): number {
  const g = AREA_GEOMETRY_BY_SLUG.get(slug);
  const radius = g?.kind === "circle" ? g.radiusUnits : undefined;
  if (radius === undefined) {
    throw new Error(
      `abilityVisuals: ability slug "${slug}" expected a circle area with a radius`,
    );
  }
  return radius;
}

function requireCone(slug: string): { arcCosThresholdBp: number; maxRangeUnits: number } {
  const g = AREA_GEOMETRY_BY_SLUG.get(slug);
  if (g?.kind === "cone" && g.arcCosThresholdBp !== undefined && g.maxRangeUnits !== undefined) {
    return { arcCosThresholdBp: g.arcCosThresholdBp, maxRangeUnits: g.maxRangeUnits };
  }
  throw new Error(`abilityVisuals: ability slug "${slug}" expected a cone area`);
}

// ─── The locked-list visual defs (#577) ─────────────────────────────────────

/**
 * Every ability's visual def, keyed by content SLUG (not the opaque id —
 * slugs are stable, human-legible, and already how `championSprites.ts`/etc.
 * key their content-derived tables). Any ability slug not present here gets
 * NO distinct visual — the locked-list scope, exactly as specified.
 */
export const ABILITY_VISUAL_DEFS_BY_SLUG: ReadonlyMap<string, AbilityVisualDef> = new Map<
  string,
  AbilityVisualDef
>([
  [
    "cone-of-cold",
    {
      archetype: "groundWedge",
      origin: "caster",
      color: 0xbfe8ff,
      alpha: 0.35,
      durationMs: DEFAULT_DURATION_MS,
      rangeUnits: requireCone("cone-of-cold").maxRangeUnits,
      arcCosThresholdBp: requireCone("cone-of-cold").arcCosThresholdBp,
    },
  ],
  [
    "frost-nova",
    {
      // Named proof-of-concept (#577) — build/validate this one first.
      archetype: "groundCircle",
      origin: "caster",
      color: 0xcfeeff,
      alpha: 0.35,
      durationMs: DEFAULT_DURATION_MS,
      radiusUnits: requireCircleRadius("frost-nova"),
    },
  ],
  [
    "meteor",
    {
      archetype: "groundCircle",
      origin: "aimPoint",
      color: 0xff5030,
      alpha: 0.35,
      durationMs: DEFAULT_DURATION_MS,
      radiusUnits: requireCircleRadius("meteor"),
    },
  ],
  [
    "rain-of-arrows",
    {
      // Same archetype as Meteor (#577), distinct amber tint (physical, not
      // fire) so the two read as different abilities at a glance.
      archetype: "groundCircle",
      origin: "aimPoint",
      color: 0xd9a441,
      alpha: 0.35,
      durationMs: DEFAULT_DURATION_MS,
      radiusUnits: requireCircleRadius("rain-of-arrows"),
    },
  ],
  [
    "shadowfury",
    {
      archetype: "groundCircle",
      origin: "aimPoint",
      color: 0x8a2be2,
      alpha: 0.4,
      // Named 150-200ms in the issue — clamped UP to the 200ms floor rather
      // than shipped at the imperceptibility edge.
      durationMs: MIN_VISUAL_DURATION_MS,
      radiusUnits: requireCircleRadius("shadowfury"),
    },
  ],
  [
    "spell-reflect-warrior",
    {
      archetype: "floatShield",
      origin: "caster",
      color: 0xbfd6ff,
      // Explicit "0.5s" in the issue.
      durationMs: 500,
    },
  ],
  [
    "bandage",
    {
      archetype: "floatGlyph",
      origin: "target",
      color: 0x54d06a,
      durationMs: DEFAULT_DURATION_MS,
      glyph: "+",
    },
  ],
  [
    "battleshout",
    {
      archetype: "floatSpeechBubble",
      origin: "caster",
      color: 0xf1c40f,
      durationMs: DEFAULT_DURATION_MS,
      glyph: "!",
    },
  ],
  [
    "unstable-affliction",
    {
      archetype: "floatGlyph",
      origin: "target",
      color: 0x8a2be2,
      durationMs: DEFAULT_DURATION_MS,
      glyph: "\u2620", // ☠ — Unicode skull, per the issue's specified glyph.
    },
  ],
]);

// ─── championId + slot → visual def ──────────────────────────────────────

const ABILITY_IDS_BY_CHAMPION: ReadonlyMap<ChampionId, readonly AbilityId[]> = new Map(
  CHAMPION_SLOTS.map((c) => [c.id, c.abilities]),
);

/**
 * Resolve the visual def (if any) for `championId`'s ability in `slot` — the
 * lookup the manager performs on every newly-observed cast onset (the `cast`
 * edge) and, since #804, on every instant-cast `castFired` event (an instant
 * resolves inside the tick it starts, so it has no cast edge at all). `null`
 * for any slot/champion not resolvable, or whose ability isn't in the #577
 * locked list — cosmetically inert, never a crash (mirrors `abilityFacts`'s
 * null-on-miss convention).
 */
export function abilityVisualDefFor(championId: ChampionId, slot: number): AbilityVisualDef | null {
  const abilityId = ABILITY_IDS_BY_CHAMPION.get(championId)?.[slot];
  if (abilityId === undefined) return null;
  const slug = ABILITY_META_BY_ID.get(abilityId)?.slug;
  if (slug === undefined) return null;
  return ABILITY_VISUAL_DEFS_BY_SLUG.get(slug) ?? null;
}

// ─── Pure lifecycle (fade + rise), mirrors `combatText.ts`'s `advanceFloater` ─

/** Fraction of `durationMs` spent fading IN before fading back out — reads
 *  as a quick flash rather than an instant pop. */
export const FADE_IN_FRACTION = 0.2;

/** Per-frame presentation of a live ability visual. */
export interface AbilityVisualFrame {
  /** Additional upward screen offset (px, positive = up), 0 for a
   *  non-rising (ground-shape) archetype. */
  readonly risePx: number;
  /** Opacity 0..1. */
  readonly opacity: number;
  /** True once the visual has outlived `durationMs` and should be removed. */
  readonly expired: boolean;
}

function clamp01(x: number): number {
  if (x < 0) return 0;
  if (x > 1) return 1;
  return x;
}

/** easeOut (quadratic) — fast start, slow finish. Mirrors `combatText.ts`'s
 *  private `easeOut` (duplicated rather than shared: the two pure lifecycle
 *  modules stay decoupled, same discipline the CC-onset/removal split uses). */
function easeOut(t: number): number {
  const c = clamp01(t);
  return 1 - (1 - c) * (1 - c);
}

/**
 * Compute an ability visual's screen rise + opacity at `nowMs`. Pure — the
 * ONE function under test for the flash's fade curve. Fades in over the
 * first `FADE_IN_FRACTION` of `durationMs`, then eases back out over the
 * remainder; rises (easeOut) toward `riseDistancePx` over its whole life
 * (`0` for a ground-shape archetype that shouldn't move).
 */
export function advanceAbilityVisual(
  spawnedMs: number,
  nowMs: number,
  durationMs: number,
  riseDistancePx: number,
): AbilityVisualFrame {
  const age = nowMs - spawnedMs;
  if (age >= durationMs) {
    return { risePx: riseDistancePx, opacity: 0, expired: true };
  }
  const t = clamp01(age / durationMs);
  const opacity =
    t < FADE_IN_FRACTION ? t / FADE_IN_FRACTION : 1 - (t - FADE_IN_FRACTION) / (1 - FADE_IN_FRACTION);
  const risePx = easeOut(t) * riseDistancePx;
  return { risePx, opacity: clamp01(opacity), expired: false };
}
